import React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { compose, lifecycle, withProps } from 'recompose';
import styled from 'styled-components';
import { Card, Row, Col, Icon, notification } from 'antd';
import moment from 'moment-timezone';

import { actions as transactionActions } from 'modules/transaction';
import { BlueButton, WithConfirmBlueButton } from 'components/Core/Buttons';
import { loadingFromStore } from 'utils/HOCs/loading';
import { dateString } from 'utils/helpers';
import { commaize } from 'utils/pricing';
import config from 'config';

const Title = styled.h3`
  margin-bottom: 6px;
  color: ${props => props.theme.orange};
`;

const TransactionInfoContainer = styled.div`
  padding-bottom: 7px;
  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.lightGray};
  }
  &:not(:first-child) {
    padding-top: 15px;
  }
`;

const ItemDetail = styled.p`
  margin-bottom: 5px;
`;

const PdfIcon = styled(Icon).attrs({
  type: 'file-pdf'
})`
  font-size: 80px;
  margin: 7px 0 15px;
`;

const CenterColumn = styled(Col)`
  text-align: center;
  @media(max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const BookingNumberTitle = styled.p`
  font-size: 14px;
`;

const BookingNumber = styled.p`
  font-size: 34px;
  font-weight: bold;
  color: ${props => props.theme.orange};
`;

const TransactionLabel = styled.p`
  margin-bottom: 10px;
`;

const ContactLabel = TransactionLabel.extend`
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.theme.red}
`;

const ContactLink = styled.a`
  font-family: Montserrat !important;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
`;

const actionRender = (transaction, option) => {
  const isPastTripDate = moment().isAfter(moment(transaction.trip_date), 'day');
  if (transaction.is_confirmed && transaction.is_booked && transaction.is_charged) {
    const voucherUrl = `${config.apiPath}/partner/transactions/${transaction._id}/voucher`;
    return [
      <PdfIcon key="pdf" />,
      <a key="download" href={voucherUrl} target="_blank"><BlueButton icon="download">Download Voucher</BlueButton></a>
    ];
  }
  if (!transaction.is_confirmed && transaction.is_booked) {
    return [
      <TransactionLabel>Trip has not been confirmed.</TransactionLabel>,
      isPastTripDate ? null : [
        <ContactLabel>Please contact support for more information.</ContactLabel>,
        <ContactLink href="tel:66830761452">Call us: +66830761452</ContactLink>
      ]
    ];
  }
  if (transaction.is_confirmed && transaction.is_booked && !transaction.is_charged) {
    return (
      <div>
        <TransactionLabel>Transaction has not been charged</TransactionLabel>
        {isPastTripDate && <WithConfirmBlueButton icon="barcode" onClick={option.recharge}>Recharged</WithConfirmBlueButton>}
      </div>
    );
  }
  if (!transaction.is_charged && !transaction.is_confirmed && !transaction.is_booked) {
    return [
      <TransactionLabel>Transaction has not been booked.</TransactionLabel>,
      isPastTripDate ? null : <ContactLabel>Please contact support for more information.</ContactLabel>
    ];
  }
  return null;
};

const enhance = compose(
  connect(
    state => ({
      transaction: state.transaction.transaction
    }),
    {...transactionActions, push}
  ),
  withProps(
    props => ({
      transactionId: props.match.params.id
    })
  ),
  withProps(
    props => ({
      recharge: async () => {
        try {
          await props.rechargeTransaction(props.transactionId);
          await props.getTransaction(props.transactionId);
        } catch (e) {
          notification.error({
            title: 'Fail to recharge',
            message: e.message,
            duration: 10
          });
        }
      }
    })
  ),
  lifecycle({
    componentDidMount() {
      this.props.getTransaction(this.props.transactionId);
    }
  }),
  loadingFromStore('transaction.isLoadingTransaction')
);

const Transaction = props => {
  const { transaction } = props;
  const { user_id: traveler, trip_id: trip, price } = transaction;
  return (
    <Card>
      <Row gutter={16}>
        <CenterColumn md={{ span: 8, push: 16 }}>
          <BookingNumberTitle>Booking Number</BookingNumberTitle>
          <BookingNumber>{transaction.booking_number}</BookingNumber>
          {actionRender(transaction, { recharge: props.recharge })}
        </CenterColumn>
        <Col md={{ span: 16, pull: 8 }}>
          <TransactionInfoContainer>
            <Title>Transaction Information</Title>
            <ItemDetail><b>Trip: </b><a href={`${config.webPath}/trip/${trip.slug}`} target="_blank">{trip.name}</a></ItemDetail>
            <ItemDetail><b>Created At: </b>{dateString(transaction.created_at, 'date')}</ItemDetail>
            <ItemDetail><b>Trip Date: </b>{dateString(transaction.trip_date, 'date')}</ItemDetail>
            <ItemDetail><b>Price: </b>THB {commaize(price.grandTotal)}</ItemDetail>
          </TransactionInfoContainer>
          <TransactionInfoContainer>
            <Title>Customer Information</Title>
            <ItemDetail><b>Name: </b>{traveler.name.first} {traveler.full_lastname}</ItemDetail>
            <ItemDetail><b>Email: </b>{traveler.email}</ItemDetail>
          </TransactionInfoContainer>
        </Col>
      </Row>
    </Card>
  );
};

export default enhance(Transaction);



// WEBPACK FOOTER //
// ./src/pages/Transaction/Transaction.js